import type { AnswersByQuestion, ContentItem } from "../layout/documentLayout";
import { flattenContent } from "../layout/documentLayout";
import { answerAsString, readField } from "../question-types/answerFormat";
import type { IcdSnapshot } from "./icdSnapshot";

// What the generated PDF prints for the entity-backed questions (patient medications, contract
// ICD codes, medical diagnosis codes, primary physician), from the live records. Mirrors the
// server: fillMedicationProfileAnswers / recursiveFillMedicationProfileItemPlaceHolderPDF,
// fillIcdAnswers / recursiveFillICDItemPlaceholderPDF / getICDAnswer (PatientDocumentsCtrl.ts) and
// mfDocumentAdapter's convertMedicalDiagnosisToMfBlock / convertPatientPhysicianToMfBlock.
// Pure (no React / app runtime) so it is node-tested.

// ---- Medications (medicationProfile) ----

export const NOT_TAKING_MEDICATIONS_TEXT = "Patient is not taking any medications";
export const MEDICATION_HEADINGS = ["Medication Name", "Dose", "Frequency"];

interface PrintedMedication {
  administrationNotes?: string | null;
  dosage: string;
  frequency: string | null;
  name: string;
}

export type MedicationPrint =
  /** The question prints as a text block with NOT_TAKING_MEDICATIONS_TEXT */
  | { type: "notTaking" }
  /** The PDF prints nothing for it */
  | { type: "none" }
  /** A chart: MEDICATION_HEADINGS, one row per medication (oldest first, as the server sends) */
  | { rows: string[][]; type: "table" };

/**
 * The server turns every medicationProfile into a chart of the v2 medications; when the patient
 * takes none, only the document's first medicationProfile prints, as a text block.
 */
export function medicationPrint(params: {
  isFirstMedicationProfile: boolean;
  medications: ReadonlyArray<PrintedMedication>;
  notTakingMedications: boolean;
}): MedicationPrint {
  if (params.notTakingMedications) {
    return params.isFirstMedicationProfile ? { type: "notTaking" } : { type: "none" };
  }
  return {
    type: "table",
    rows: params.medications.map((medication) => [
      medication.name,
      medication.dosage,
      // The server writes " " for a missing frequency
      medication.frequency ?? " ",
    ]),
  };
}

/**
 * CMS485 / FC_CMS485 print their only top-level medicationProfile as a text block instead of a
 * chart (fillMedicationProfileAnswers); any other one prints the usual way.
 */
export function printsMedicationsAsText(
  documentType: string,
  content: ContentItem[],
  questionId: number
): boolean {
  if (documentType !== "CMS485" && documentType !== "FC_CMS485") {
    return false;
  }
  const topLevel = content.filter((item) => item.itemType === "medicationProfile");
  return topLevel.length === 1 && topLevel[0].id === questionId;
}

/** That text block: one line per medication, "name, dosage, frequency, administration notes". */
export function medicationProfileText(params: {
  medications: ReadonlyArray<PrintedMedication>;
  notTakingMedications: boolean;
}): string {
  if (params.notTakingMedications) {
    return NOT_TAKING_MEDICATIONS_TEXT;
  }
  return params.medications
    .map((medication) =>
      [medication.name, medication.dosage, medication.frequency, medication.administrationNotes]
        .filter((value) => value !== null && value !== undefined)
        .join(", ")
    )
    .join("\n");
}

/** The server's `getContentItem(content, ..., true)[0]`: depth-first, parents first. */
export function firstItemOfTypes(
  content: ContentItem[],
  itemTypes: ReadonlyArray<ContentItem["itemType"]>
): ContentItem | null {
  return flattenContent(content).find((item) => itemTypes.includes(item.itemType)) ?? null;
}

// ---- Contract ICD codes (icdCodes / finalIcdCodes) ----

/**
 * The question whose stored answer the PDF reads for the codes (getICDAnswer: the first top-level
 * icdCodes), else the first finalIcdCodes. The review rewrites this answer after each code change
 * so the PDF follows the live codes.
 */
export function icdSnapshotQuestionId(content: ContentItem[]): number | null {
  const icdCodes = content.find((item) => item.itemType === "icdCodes");
  if (icdCodes !== undefined) {
    return icdCodes.id;
  }
  return firstItemOfTypes(content, ["finalIcdCodes"])?.id ?? null;
}

/**
 * What an icdCodes / finalIcdCodes question prints, as the snapshot for
 * PatientContractDiagnosisCodesView (`null` = the task contract's live codes):
 * - icdCodes: the codes of getICDAnswer (the first top-level icdCodes' answer), else the live codes;
 * - the document's first ICD question (icdCodes or finalIcdCodes): the same codes;
 * - any other finalIcdCodes: its own stored answer, listed only when it has exactly one row.
 */
export function icdViewSnapshot(
  question: ContentItem,
  content: ContentItem[],
  answers: AnswersByQuestion
): IcdSnapshot {
  const pdfSource = content.find((item) => item.itemType === "icdCodes");
  const pdfSourceRow = pdfSource === undefined ? undefined : answers.get(pdfSource.id)?.[0];
  const pdfCodes: IcdSnapshot = pdfSourceRow === undefined ? null : { answer: pdfSourceRow.answer };

  if (question.itemType === "icdCodes") {
    return pdfCodes;
  }
  if (firstItemOfTypes(content, ["icdCodes", "finalIcdCodes"])?.id === question.id) {
    return pdfCodes;
  }
  const rows = answers.get(question.id) ?? [];
  return { answer: rows.length === 1 ? rows[0].answer : null };
}

/** A GENERAL document's icdCodes prints as a chart with these columns. */
export const ICD_CHART_HEADINGS = ["DX Code", "Type", "Description", "Start Date", "End Date"];

const ICD_CHART_TYPE_LABELS: Record<string, string> = {
  PRINCIPAL: "Principal Diagnosis",
  SURGICAL: "Surgical Procedure",
  OTHER: "Other Pertinent Diagnoses",
};

/** js-joda's "MM/d/y" (e.g. 10/1/2026) from a LocalDate or "yyyy-MM-dd..." text. */
export function formatIcdChartDate(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  return match === null ? String(value) : `${match[2]}/${Number(match[3])}/${match[1]}`;
}

/** recursiveFillICDItemPlaceholderPDF: one chart row per code, in the given order. */
export function buildIcdChartRows(codes: unknown[]): string[][] {
  return codes.map((code) => {
    const type = answerAsString(readField(code, "type")) ?? "OTHER";
    return [
      answerAsString(readField(code, "formattedDxCode")) ?? "",
      ICD_CHART_TYPE_LABELS[type] ?? "",
      answerAsString(readField(code, "shortDesc")) ?? "",
      formatIcdChartDate(readField(code, "startDate")),
      formatIcdChartDate(readField(code, "endDate")),
    ];
  });
}

// ---- Medical diagnosis codes (medicalDiagnosis) ----

export const MEDICAL_DIAGNOSIS_HEADINGS = ["Designation", "DX Code", "Description"];

/** convertMedicalDiagnosisToMfBlock: no columns at all when the patient has no codes. */
export function medicalDiagnosisPrint(
  codes: ReadonlyArray<{ designation: string; formattedDxCode: string; shortDesc: string }>
): { headings: string[]; rows: string[][] } {
  if (codes.length === 0) {
    return { headings: [], rows: [] };
  }
  return {
    headings: MEDICAL_DIAGNOSIS_HEADINGS,
    rows: codes.map((code) => [code.designation, code.formattedDxCode, code.shortDesc]),
  };
}

// ---- Primary physician (patientPhysician) ----

export const PHYSICIAN_TITLE = "Physician Details";
export const NO_PHYSICIAN_TEXT = "No physician selected";

export interface PrintedPhysician {
  address: string | null;
  faxNumber: string | null;
  firstName: string;
  lastName: string;
  npi: string | null;
  phone: string | null;
}

/**
 * convertPatientPhysicianToMfBlock: Name and NPI always, Address / Phone Number / Fax Number when
 * set. `formatPhone` formats an E.164 number for display (the server prints others as is).
 */
export function physicianPrintLines(
  physician: PrintedPhysician,
  formatPhone: (phone: string) => string
): { key: string; value: string }[] {
  const lines = [
    { key: "Name", value: `${physician.firstName} ${physician.lastName}` },
    { key: "NPI", value: physician.npi ?? "Empty" },
  ];
  if (physician.address !== null) {
    lines.push({ key: "Address", value: physician.address });
  }
  if (physician.phone !== null) {
    lines.push({ key: "Phone Number", value: formatPhone(physician.phone) });
  }
  if (physician.faxNumber !== null) {
    lines.push({ key: "Fax Number", value: physician.faxNumber });
  }
  return lines;
}
